import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { formatTime } from '@/lib/format';
import { weatherConditionInfo } from '@/lib/weather';
import type { TyreFastestLap } from '@/types/telemetry';

interface TyreHistoryTableProps {
  laps: TyreFastestLap[];
}

export function TyreHistoryTable({ laps }: TyreHistoryTableProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const sorted = [...laps].sort((a, b) => a.lapTimeMs - b.lapTimeMs);

  if (sorted.length === 0) {
    return (
      <div className="card flex flex-col items-center justify-center py-12 gap-2 text-center">
        <span className="text-2xl opacity-60">🏁</span>
        <p className="text-sm text-text-secondary">{t('tyre.noFastestLaps')}</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Desktop table */}
      <table className="w-full text-sm border-collapse hidden md:table">
        <thead>
          <tr className="divider">
            <th className="text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.trackColumn')}</th>
            <th className="text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.bestLapColumn')}</th>
            <th className="text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.dateColumn')}</th>
            <th className="text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.ageColumn')}</th>
            <th className="text-center text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.weatherColumn')}</th>
            <th className="text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.tempColumn')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((lap, i) => {
            const weather = weatherConditionInfo(lap.weatherCondition, t);
            return (
              <tr
                key={`${lap.trackId}-${i}`}
                onClick={() => navigate('#/session/' + lap.sessionId)}
                className={`hover:bg-bg-hover transition-colors cursor-pointer ${i < sorted.length - 1 ? 'divider' : ''}`}
              >
                <td className="px-4 py-3 text-text-primary font-medium">{lap.trackName}</td>
                <td className="px-4 py-3 text-right mono text-green font-semibold">{formatTime(lap.lapTimeMs)}</td>
                <td className="px-4 py-3 text-right text-text-secondary mono">{lap.sessionDate}</td>
                <td className="px-4 py-3 text-right text-text-secondary mono">{t('tyre.ageLaps').replace('{n}', String(lap.tyreAgeLaps))}</td>
                <td className="px-4 py-3 text-center" title={weather.label}>{weather.icon}</td>
                <td className="px-4 py-3 text-right text-text-secondary mono">{lap.temperatureC}°C</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile cards */}
      <div className="md:hidden">
        {sorted.map((lap, i) => {
          const weather = weatherConditionInfo(lap.weatherCondition, t);
          return (
            <div
              key={`${lap.trackId}-${i}`}
              onClick={() => navigate('#/session/' + lap.sessionId)}
              className={`p-4 hover:bg-bg-hover transition-colors cursor-pointer ${i < sorted.length - 1 ? 'divider' : ''}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-text-primary">{lap.trackName}</span>
                <span className="mono text-sm text-green font-semibold">{formatTime(lap.lapTimeMs)}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-text-secondary">
                <span className="mono">{lap.sessionDate}</span>
                <span className="mono">{t('tyre.ageLaps').replace('{n}', String(lap.tyreAgeLaps))}</span>
                <span className="mono">{weather.icon} {lap.temperatureC}°C</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
